import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { arrivedViaRecovery, urlHasRecoveryToken } from '../lib/recoveryFlow';

// Safety net for the password-reset flow. Renders nothing.
//
// A reset email sends the user through Supabase's /auth/v1/verify
// endpoint, which mints a recovery session and redirects back to the
// app. The "set a new password" form lives at /reset-password — but
// Supabase only honors that redirect target when it's on the
// dashboard's allow-list. When it isn't, Supabase falls back to the
// Site URL and the user lands on `/` holding a live recovery session:
// ProtectedRoute treats them as logged in and forwards them to the
// dashboard, so they never see the form.
//
// This component routes any recovery flow to /reset-password regardless
// of where the email link dropped the user, so a redirect-URL config
// gap can't silently break password reset.
export default function RecoveryRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    function goToReset() {
      if (window.location.pathname !== '/reset-password') {
        navigate('/reset-password', { replace: true });
      }
    }

    // `arrivedViaRecovery` is the reliable signal (captured at module
    // load, before Supabase strips the token). The live re-check covers
    // the unlikely case where the snapshot missed but the token is
    // somehow still in the URL when this effect runs.
    if (arrivedViaRecovery || urlHasRecoveryToken()) goToReset();

    // Secondary catch: honor a PASSWORD_RECOVERY event if Supabase
    // happens to fire one after this listener is attached.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') goToReset();
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  return null;
}
